import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface DealRevenueTabProps {
  kpis: any;
  onKpisChange: (data: any) => void;
  currency: string;
}

export function DealRevenueTab({
  kpis,
  onKpisChange,
  currency,
}: DealRevenueTabProps) {
  const handleRoomRevChange = (
    field: "adr" | "occ",
    index: number,
    value: number,
  ) => {
    const currentRoomRev = kpis.roomRev || {};
    const currentRoomRevenue = kpis.roomRevenue || {};

    const newRoomRevArray = [
      ...(currentRoomRev[field] || Array(12).fill(field === "adr" ? 100 : 70)),
    ];
    newRoomRevArray[index] = value;

    const newRoomRevenueArray = [
      ...(currentRoomRevenue[field] ||
        Array(12).fill(field === "adr" ? 100 : 70)),
    ];
    newRoomRevenueArray[index] = value;

    onKpisChange({
      ...kpis,
      roomRev: {
        ...currentRoomRev,
        [field]: newRoomRevArray,
      },
      roomRevenue: {
        ...currentRoomRevenue,
        [field]: newRoomRevenueArray,
      },
    });
  };

  const handleFnbChange = (path: string, value: number) => {
    const keys = path.split(".");
    const updatedKpis = { ...kpis };
    let current: any = updatedKpis;

    for (let i = 0; i < keys.length; i++) {
      if (i === keys.length - 1) {
        current[keys[i]] = value;
      } else {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
    }

    onKpisChange(updatedKpis);
  };

  const handleOtherRevChange = (section: string, updates: any) => {
    const currentOtherRev = kpis.otherRev || {};

    onKpisChange({
      ...kpis,
      otherRev: {
        ...currentOtherRev,
        [section]: {
          ...(currentOtherRev[section] || {}),
          ...updates,
        },
      },
    });
  };

  return (
    <TabsContent value="revenue" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Room Revenue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium">
              Monthly ADR ({currency})
            </Label>
            <div className="grid grid-cols-6 gap-3 mt-3">
              {months.map((month, i) => (
                <div key={month} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {month}
                  </Label>
                  <Input
                    type="number"
                    className="h-9"
                    value={
                      kpis.roomRev?.adr?.[i] ||
                      kpis.roomRevenue?.adr?.[i] ||
                      100
                    }
                    onChange={(e) =>
                      handleRoomRevChange("adr", i, Number(e.target.value))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Monthly Occupancy (%)</Label>
            <div className="grid grid-cols-6 gap-3 mt-3">
              {months.map((month, i) => (
                <div key={month} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {month}
                  </Label>
                  <Input
                    type="number"
                    className="h-9"
                    value={
                      kpis.roomRev?.occ?.[i] || kpis.roomRevenue?.occ?.[i] || 70
                    }
                    onChange={(e) =>
                      handleRoomRevChange("occ", i, Number(e.target.value))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>F&B Revenue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Average Guests per Room</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                step="0.1"
                className="w-32"
                value={kpis.fnb?.avgGuests || 2.8}
                onChange={(e) =>
                  handleFnbChange("fnb.avgGuests", Number(e.target.value))
                }
              />
              <span className="text-sm text-muted-foreground">
                Typical: 1.6-2.0 for resorts, 1.8-2.2 for city hotels
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">
              Meal Services - Capture Rate (%)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {(["breakfast", "lunch", "dinner", "bar"] as const).map(
                (meal) => (
                  <Card key={meal} className="border">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm capitalize">
                        {meal}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Capture Rate (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={kpis.fnb?.capture?.[meal] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              handleFnbChange(`fnb.capture.${meal}`, 0);
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                handleFnbChange(
                                  `fnb.capture.${meal}`,
                                  Math.min(100, Math.max(0, numValue)),
                                );
                              }
                            }
                          }}
                          placeholder="50"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">External Covers</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={kpis.fnb?.externalCovers?.[meal] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              handleFnbChange(`fnb.externalCovers.${meal}`, 0);
                            } else {
                              const numValue = parseInt(value);
                              if (!isNaN(numValue)) {
                                handleFnbChange(
                                  `fnb.externalCovers.${meal}`,
                                  Math.max(0, numValue),
                                );
                              }
                            }
                          }}
                          placeholder="0"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Avg Check - Guests</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={kpis.fnb?.check?.guest?.[meal] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              handleFnbChange(`fnb.check.guest.${meal}`, 0);
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue) && numValue >= 0) {
                                handleFnbChange(
                                  `fnb.check.guest.${meal}`,
                                  numValue,
                                );
                              }
                            }
                          }}
                          placeholder="20"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ),
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Spa & Wellness Revenue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Treatments per Day</Label>
              <Input
                type="number"
                value={kpis.otherRev?.wellness?.treatmentsPerDay || 4}
                onChange={(e) =>
                  handleOtherRevChange("wellness", {
                    treatmentsPerDay: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Average Treatment Price ({currency})</Label>
              <Input
                type="number"
                value={kpis.otherRev?.wellness?.avgPrice || 70}
                onChange={(e) =>
                  handleOtherRevChange("wellness", {
                    avgPrice: Number(e.target.value),
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Other Revenue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Revenue Model</Label>
              <Select
                value={kpis.otherRev?.other?.mode || "pctRooms"}
                onValueChange={(value) =>
                  handleOtherRevChange("other", { mode: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pctRooms">% of Rooms Revenue</SelectItem>
                  <SelectItem value="fixed">Monthly Fixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!kpis.otherRev?.other?.mode ||
            kpis.otherRev.other.mode === "pctRooms" ? (
              <div className="space-y-2">
                <Label>Percentage of Rooms Revenue (%)</Label>
                <Input
                  type="number"
                  value={kpis.otherRev?.other?.value || 100}
                  onChange={(e) =>
                    handleOtherRevChange("other", {
                      value: Number(e.target.value),
                    })
                  }
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Monthly Revenue ({currency})</Label>
                <Input
                  type="number"
                  value={kpis.otherRev?.other?.value || 2000}
                  onChange={(e) =>
                    handleOtherRevChange("other", {
                      value: Number(e.target.value),
                    })
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
}
